import { useEffect, useState } from 'react';
import api from '../../../api/Base';
import HostTarget from '../../../types/host/HostTarget';
import HostDiskVolume from '../../../types/host/HostDiskVolume';
import HostDiskVolumeData from '../../../types/host/HostDiskVolumeData';
import { EChartTimeSeries } from '../../chart/echarts/types';
import { useTimeRange } from '../../../context/TimeRangeContext';
import { buildApiQuery, getConvertedTimeToUTC } from '../../../helpers/timeRangeUtils';
import { getErrorMessage, showError } from '../../../utils/showAlert';
import { CHART_COLOURS_STATEMENTS, DATA_FAILED, DATA_LOADED, DATA_LOADING } from '../../Constants';

const KB_PER_GB = 1048576;

export interface DiskVolumeSeries {
    disk: string;
    series: EChartTimeSeries[];
}

interface DiskVolumeStatisticResult {
    loading: number;
    basicSeries: EChartTimeSeries[];
    volumeSeries: DiskVolumeSeries[];
}

export const useDiskVolumeStatistic = (host: HostTarget): DiskVolumeStatisticResult => {
    const [loading, setLoading] = useState<number>(DATA_LOADING);
    const [basicSeries, setBasicSeries] = useState<EChartTimeSeries[]>([]);
    const [volumeSeries, setVolumeSeries] = useState<DiskVolumeSeries[]>([]);
    const { timeRange } = useTimeRange();

    useEffect(() => {
        let cancelled = false;
        setLoading(DATA_LOADING);

        const query = buildApiQuery(timeRange);
        const limit = process.env.REACT_APP_API_LIMIT;

        api.get(`disk/statistic/disk?${query}&id=${host.id}`)
            .then(response => {
                const volumes: HostDiskVolume[] = response.data ?? [];
                if (!volumes.length) {
                    return [];
                }
                return Promise.all(volumes.map((volume, index) =>
                    api.get(`disk/statistic/time?${query}&limit=${limit}&id=${host.id}&disk=${volume.disk}`)
                        .then((volumeResponse: { data: HostDiskVolumeData[] }) => {
                            const rows: any[] = volumeResponse.data ?? [];
                            return {
                                disk: volume.disk,
                                utilisation: {
                                    name: volume.disk,
                                    chartType: 'line',
                                    color: CHART_COLOURS_STATEMENTS[index],
                                    data: rows.map(row => [getConvertedTimeToUTC(row), Number(row.diskutilisation) || 0]),
                                } as EChartTimeSeries,
                                breakdown: [{
                                    name: `${volume.disk} Free Space (GB)`,
                                    chartType: 'bar',
                                    color: CHART_COLOURS_STATEMENTS[0],
                                    data: rows.map(row => [getConvertedTimeToUTC(row), (Number(row['K available disk space']) || 0) / KB_PER_GB]),
                                }, {
                                    name: `${volume.disk} Used Space (GB)`,
                                    chartType: 'bar',
                                    color: CHART_COLOURS_STATEMENTS[1],
                                    data: rows.map(row => [getConvertedTimeToUTC(row), (Number(row['K used disk space']) || 0) / KB_PER_GB]),
                                }] as EChartTimeSeries[],
                            };
                        })
                ));
            })
            .then(volumes => {
                if (cancelled || !volumes) {
                    return;
                }
                setBasicSeries(volumes.map(volume => volume.utilisation));
                setVolumeSeries(volumes.map(volume => ({ disk: volume.disk, series: volume.breakdown })));
                setLoading(DATA_LOADED);
            })
            .catch(error => {
                if (cancelled) {
                    return;
                }
                showError('Failed to retrieve disk statistic time', getErrorMessage(error));
                setLoading(DATA_FAILED);
            });

        return () => {
            cancelled = true;
        };
    }, [timeRange.from.valueOf(), timeRange.to.valueOf(), timeRange.interval, host]);

    return { loading, basicSeries, volumeSeries };
};
